import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';


import { RefreshCw, AlertCircle, Link, Search, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { WarningIcon } from '@phosphor-icons/react';
import { apiFetch } from '@/utils/api';
import { isFeatureEnabled } from '@/utils/featureFlags';
import PaginationFooter from '@/components/PaginationFooter';
import { useI18n } from '@/hooks/useI18n';

interface GoogleDriveFile {
    id: string;
    organization_id: string;
    file_name: string;
    google_file_id: string;
    local_file_path?: string;
    mime_type?: string;
    drive_modified_time: string;
    extracted_invoice_number?: string;
    processing_status: string;
    error_message: string | null;
    created_at: string;
    cost_center_id: string;
    matched_invoice_id?: string | null;
    document_type?: 'invoice' | 'delivery_note' | null;
    ai_result_json?: string | null;
}

interface Pagination {
    page: number;
    total: number;
    pages: number;
    limit: number;
}

// Helper function to extract vendor name from ai_result_json
const getVendorName = (ai_result_json?: string | null): string => {
    if (!ai_result_json) return '';
    try {
        const aiResult = JSON.parse(ai_result_json);
        return aiResult?.vendor?.name || '';
    } catch (error) {
        return '';
    }
};

export default function GDriveFilesPage() {
    const navigate = useNavigate();
    const routerState = useRouterState();
    const searchParams = routerState.location.search as any;
    const { t } = useI18n();
    const [files, setFiles] = useState<GoogleDriveFile[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [refreshing, setRefreshing] = useState<boolean>(false);
    const [pagination, setPagination] = useState<Pagination>({
        page: 1,
        total: 0,
        pages: 0,
        limit: 20,
    });

    // Filters and sorting
    const [filters, setFilters] = useState<{ search: string; status: string; extracted_invoice_number: string; has_extracted: string }>(
        { search: '', status: '', extracted_invoice_number: '', has_extracted: '' }
    );
    const [dateFilters, setDateFilters] = useState<{ date_from?: string; date_to?: string }>({});
    const [hideMatched, setHideMatched] = useState<boolean>(false);
    const [sortBy, setSortBy] = useState<string>('drive_modified_time');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

    const [showAdminSyncButtons, setShowAdminSyncButtons] = useState<boolean>(true);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);

    // Debounced search
    const [debouncedSearch, setDebouncedSearch] = useState<string>('');

    // Sync state from URL parameters whenever they change
    useEffect(() => {
        const {
            page, limit, hide_matched,
            search, status_filter, extracted_invoice_number, has_extracted,
            date_from, date_to,
            sort_by, sort_dir,
        } = searchParams || {};

        // Update pagination from URL
        const urlPage = parseInt((page as string) || '1', 10);
        const urlLimit = parseInt((limit as string) || '20', 10);
        setPagination(prev => ({
            ...prev,
            page: urlPage,
            limit: urlLimit,
        }));

        // Update filters from URL
        setFilters({
            search: (search as string) || '',
            status: (status_filter as string) || '',
            extracted_invoice_number: (extracted_invoice_number as string) || '',
            has_extracted: String(has_extracted || ''),
        });

        setDateFilters({
            date_from: (date_from as string) || undefined,
            date_to: (date_to as string) || undefined,
        });

        // Sorting from URL
        setSortBy(sort_by ? String(sort_by) : 'drive_modified_time');
        setSortDir(sort_dir && String(sort_dir).toLowerCase() === 'asc' ? 'asc' : 'desc');

        // Update hideMatched from URL - handle both boolean and string values
        setHideMatched(hide_matched === true || hide_matched === 'true');

        // Set debounced search immediately from URL to avoid delay
        setDebouncedSearch((search as string) || '');

        // Mark as initialized after first run
        if (!isInitialized) {
            setIsInitialized(true);
        }
    }, [searchParams]);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(filters.search);
        }, 300);
        return () => clearTimeout(timer);
    }, [filters.search]);

    useEffect(() => {
        if (isInitialized) {
            loadFiles();
        }
    }, [
        isInitialized,
        pagination.page,
        pagination.limit,
        hideMatched,
        debouncedSearch,
        filters.status,
        filters.extracted_invoice_number,
        filters.has_extracted,
        dateFilters.date_from,
        dateFilters.date_to,
        sortBy,
        sortDir,
    ]);

    useEffect(() => {
        // Load feature flags
        const loadFeatureFlags = async () => {
            try {
                const enabled = await isFeatureEnabled('enable_admin_sync_buttons');
                setShowAdminSyncButtons(enabled);
            } catch (error) {
                console.error('Error loading feature flags:', error);
                // Default to true on error
                setShowAdminSyncButtons(true);
            }
        };

        loadFeatureFlags();
    }, []);

    // Update URL with current state
    const updateURL = (
        newPagination?: Partial<Pagination>,
        newHideMatched?: boolean,
        newFilters?: Partial<typeof filters>,
        newDateFilters?: typeof dateFilters,
        overrideSortBy?: string,
        overrideSortDir?: 'asc' | 'desc',
    ) => {
        const currentPagination = newPagination ? { ...pagination, ...newPagination } : pagination;
        const currentHideMatched = newHideMatched !== undefined ? newHideMatched : hideMatched;
        const currentFilters = newFilters ? { ...filters, ...newFilters } : filters;
        const currentDateFilters = newDateFilters !== undefined ? newDateFilters : dateFilters;

        const params = new URLSearchParams();

        // Pagination
        params.set('page', currentPagination.page.toString());
        params.set('limit', currentPagination.limit.toString());

        // Sorting
        const effectiveSortBy = overrideSortBy ?? sortBy;
        const effectiveSortDir = overrideSortDir ?? sortDir;
        if (effectiveSortBy) params.set('sort_by', effectiveSortBy);
        if (effectiveSortDir) params.set('sort_dir', effectiveSortDir);

        // Hide matched
        params.set('hide_matched', currentHideMatched.toString());

        // Filters
        if (currentFilters.search) params.set('search', currentFilters.search);
        if (currentFilters.status) params.set('status_filter', currentFilters.status);
        if (currentFilters.extracted_invoice_number) params.set('extracted_invoice_number', currentFilters.extracted_invoice_number);
        if (currentFilters.has_extracted) params.set('has_extracted', currentFilters.has_extracted);

        // Date filters
        if (currentDateFilters.date_from) params.set('date_from', currentDateFilters.date_from);
        if (currentDateFilters.date_to) params.set('date_to', currentDateFilters.date_to);

        const newUrl = `${routerState.location.pathname}?${params.toString()}`;
        navigate({ to: newUrl, replace: true });
    };

    const loadFiles = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: pagination.page.toString(),
                limit: pagination.limit.toString(),
                hide_matched: hideMatched ? 'true' : 'false',
            });

            // Sorting
            params.set('sort_by', sortBy);
            params.set('sort_dir', sortDir);

            // Filters
            if (filters.status) params.set('status_filter', filters.status);
            if (debouncedSearch) params.set('search', debouncedSearch);
            if (filters.extracted_invoice_number) params.set('extracted_invoice_number', filters.extracted_invoice_number);
            if (filters.has_extracted) params.set('has_extracted', filters.has_extracted);
            if (dateFilters.date_from) params.set('date_from', dateFilters.date_from);
            if (dateFilters.date_to) params.set('date_to', dateFilters.date_to);

            const response = await apiFetch(`/api/gdrive-invoices/files?${params}`);

            if (response.ok) {
                const data = await response.json();
                setFiles(data.files || data);
                if (data.pagination) {
                    setPagination(prev => ({ ...prev, ...data.pagination }));
                }
            } else {
                console.error('Failed to load Google Drive files:', response.status);
            }
        } catch (error) {
            console.error('Error loading Google Drive files:', error);

        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key: keyof typeof filters, value: string) => {
        const newFilters = { ...filters, [key]: value };
        const newPagination = { ...pagination, page: 1 };
        setFilters(newFilters);
        setPagination(newPagination);
        updateURL(newPagination, undefined, newFilters);
    };

    const handleDateFiltersChange = (key: keyof typeof dateFilters, value: string) => {
        const newDateFilters = { ...dateFilters, [key]: value };
        const newPagination = { ...pagination, page: 1 };
        setDateFilters(newDateFilters);
        setPagination(newPagination);
        updateURL(newPagination, undefined, undefined, newDateFilters);
    };

    const clearFilters = () => {
        const resetFilters = { search: '', status: '', extracted_invoice_number: '', has_extracted: '' };
        const resetDates: typeof dateFilters = {};
        const newPagination = { ...pagination, page: 1 };
        setFilters(resetFilters);
        setDateFilters(resetDates);
        setHideMatched(false);
        setPagination(newPagination);
        updateURL(newPagination, false, resetFilters, resetDates, 'drive_modified_time', 'desc');
        setSortBy('drive_modified_time');
        setSortDir('desc');
    };

    const handleSort = (column: string) => {
        const newDir: 'asc' | 'desc' = sortBy === column ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';
        setSortBy(column);
        setSortDir(newDir);
        const newPagination = { ...pagination, page: 1 };
        setPagination(newPagination);
        updateURL(newPagination, undefined, undefined, undefined, column, newDir);
    };



    const handleRefresh = async () => {
        setRefreshing(true);
        await loadFiles();
        setRefreshing(false);
    };

    const handleManualMatch = (file: GoogleDriveFile) => {
        // Build return URL with current state
        const params = new URLSearchParams();
        params.set('page', pagination.page.toString());
        params.set('limit', pagination.limit.toString());
        params.set('hide_matched', hideMatched.toString());
        // Sorting
        params.set('sort_by', sortBy);
        params.set('sort_dir', sortDir);
        // Filters
        if (filters.search) params.set('search', filters.search);
        if (filters.status) params.set('status_filter', filters.status);
        if (filters.extracted_invoice_number) params.set('extracted_invoice_number', filters.extracted_invoice_number);
        if (filters.has_extracted) params.set('has_extracted', filters.has_extracted);
        if (dateFilters.date_from) params.set('date_from', dateFilters.date_from);
        if (dateFilters.date_to) params.set('date_to', dateFilters.date_to);

        const returnTo = `/invoices/gdrive-files?${params.toString()}`;
        navigate({
            to: `/invoices/manual-match`,
            search: {
                fileId: file.id,
                returnTo: returnTo
            } as any
        });
    };

    const [orgSyncing, setOrgSyncing] = useState<boolean>(false);
    const handleOrgSync = async () => {
        try {
            setOrgSyncing(true);
            const resp = await apiFetch('/api/gdrive-invoices/sync-organization', {
                method: 'POST',
            });
            if (!resp.ok) {
                const data = await resp.json().catch(() => ({}));
                alert(data.detail || 'Failed to start organization sync');
                return;
            }
            // Optionally poll or just inform user
            // Give user feedback and refresh list after a short delay
            setTimeout(() => {
                loadFiles();
            }, 1500);
        } catch (e) {
            console.error('Error starting organization sync', e);
            alert('Failed to start organization sync due to network error');
        } finally {
            setOrgSyncing(false);
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'pending':
                return <Badge variant="outline">{t('gdriveFiles.status.pending', 'finance')}</Badge>;
            case 'processed':
                return <Badge variant="secondary">{t('gdriveFiles.status.processed', 'finance')}</Badge>;
            case 'matched':
                return <Badge variant="default">{t('gdriveFiles.status.matched', 'finance')}</Badge>;
            case 'failed':
                return <Badge variant="destructive">{t('gdriveFiles.status.failed', 'finance')}</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };



    return (
        <Layout>
            <div className="space-y-6">
                <div>
                    <h1 className="text-3xl font-bold">{t('gdriveFiles.title', 'finance')}</h1>
                    <p className="text-muted-foreground mt-2">
                        {t('gdriveFiles.subtitle', 'finance')}
                    </p>
                </div>

                {/* Filters */}
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        {/* Search */}
                        <div className="flex-1">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder={t('gdriveFiles.searchPlaceholder', 'finance')}
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                    className="pl-8"
                                />
                            </div>
                        </div>

                        {/* Status Filter */}
                        <div>
                            <Select
                                value={filters.status || 'all'}
                                onValueChange={(value) => handleFilterChange('status', value === 'all' ? '' : value)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder={t('invoices.status', 'finance')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('gdriveFiles.allStatuses', 'finance')}</SelectItem>
                                    <SelectItem value="pending">{t('gdriveFiles.status.pending', 'finance')}</SelectItem>
                                    <SelectItem value="processed">{t('gdriveFiles.status.processed', 'finance')}</SelectItem>
                                    <SelectItem value="matched">{t('gdriveFiles.status.matched', 'finance')}</SelectItem>
                                    <SelectItem value="failed">{t('gdriveFiles.status.failed', 'finance')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Advanced Filters */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                        <div>
                            <label className="text-sm leading-none font-medium">{t('forms.dateFrom', 'common')}</label>
                            <Input
                                type="date"
                                value={dateFilters.date_from || ''}
                                onChange={(e) => handleDateFiltersChange('date_from', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="text-sm leading-none font-medium">{t('forms.dateTo', 'common')}</label>
                            <Input
                                type="date"
                                value={dateFilters.date_to || ''}
                                onChange={(e) => handleDateFiltersChange('date_to', e.target.value)}
                            />
                        </div>

                        <div>
                            <label className="text-sm leading-none font-medium">{t('gdriveFiles.hasExtractedInvoiceNumber', 'finance')}</label>
                            <Select
                                value={filters.has_extracted || 'all'}
                                onValueChange={(value) => handleFilterChange('has_extracted', value === 'all' ? '' : value)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder={t('gdriveFiles.hasExtracted', 'finance')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('gdriveFiles.filters.all', 'finance')}</SelectItem>
                                    <SelectItem value="true">{t('gdriveFiles.filters.yes', 'finance')}</SelectItem>
                                    <SelectItem value="false">{t('gdriveFiles.filters.no', 'finance')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-sm leading-none font-medium pb-4">{t('gdriveFiles.hideMatched', 'finance')}</label><br />
                                <input
                                    type="checkbox"
                                    checked={hideMatched}
                                    onChange={(e) => {
                                        const newHideMatched = e.target.checked;
                                        setHideMatched(newHideMatched);
                                        updateURL({ page: 1 }, newHideMatched); // Reset to page 1 when changing filter
                                    }}
                                />
                        </div>
                    </div>

                    {/* Clear Filters */}
                    {(filters.status || filters.search || filters.has_extracted || hideMatched || dateFilters.date_from || dateFilters.date_to) && (
                        <div className="flex justify-end">
                            <Button variant="outline" size="sm" onClick={clearFilters}>{t('actions.clearAllFilters', 'common')}</Button>
                        </div>
                    )}
                </div>

                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-medium"></h3>
                        <div className="flex items-center gap-3">

                            <Button
                                size="sm"
                                variant="outline"
                                onClick={handleRefresh}
                                disabled={refreshing}
                            >
                                {refreshing ? (
                                    <RefreshCw className="h-4 w-4 animate-spin mr-1" />
                                ) : (
                                    <RefreshCw className="h-4 w-4 mr-1" />
                                )}
                                {refreshing ? t('gdriveFiles.refreshing', 'finance') : t('gdriveFiles.refresh', 'finance')}
                            </Button>
                            {showAdminSyncButtons && (
                                <Button
                                    size="sm"
                                    onClick={handleOrgSync}
                                    disabled={orgSyncing}
                                >
                                    {orgSyncing ? (
                                        <RefreshCw className="h-4 w-4 animate-spin mr-1" />
                                    ) : (
                                        <RefreshCw className="h-4 w-4 mr-1" />
                                    )}
                                    {orgSyncing ? t('gdriveFiles.syncingOrg', 'finance') : t('gdriveFiles.syncEntireOrg', 'finance')}
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className="min-h-[600px] relative">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead onClick={() => handleSort('file_name')} className="cursor-pointer select-none w-120">
                                        <span className="inline-flex items-center gap-1">
                                            {t('gdriveFiles.fileName', 'finance')} {sortBy === 'file_name' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                        </span>
                                    </TableHead>
                                    <TableHead className="w-40">
                                        {t('gdriveFiles.vendorName', 'finance')}
                                    </TableHead>
                                    <TableHead onClick={() => handleSort('drive_modified_time')} className="cursor-pointer select-none w-41">
                                        <span className="inline-flex items-center gap-1">
                                            {t('gdriveFiles.originalDate', 'finance')} {sortBy === 'drive_modified_time' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                        </span>
                                    </TableHead>
                                    <TableHead onClick={() => handleSort('document_type')} className="cursor-pointer select-none w-24">
                                        <span className="inline-flex items-center gap-1">
                                            {t('gdriveFiles.documentType', 'finance')} {sortBy === 'document_type' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                        </span>
                                    </TableHead>
                                    <TableHead onClick={() => handleSort('extracted_invoice_number')} className="cursor-pointer select-none w-32">
                                        <span className="inline-flex items-center gap-1">
                                            {t('gdriveFiles.extractedInvoiceNumber', 'finance')} {sortBy === 'extracted_invoice_number' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                        </span>
                                    </TableHead>
                                    <TableHead onClick={() => handleSort('processing_status')} className="cursor-pointer select-none w-20">
                                        <span className="inline-flex items-center gap-1">
                                            {t('invoices.status', 'finance')} {sortBy === 'processing_status' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                        </span>
                                    </TableHead>
                                    <TableHead className="text-right w-24"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    Array.from({ length: Math.min(pagination.limit || 10, 10) }).map((_, i) => (
                                        <TableRow key={`skeleton-${i}`}>
                                            <TableCell><div className="h-4 w-120 bg-muted animate-pulse rounded" /></TableCell>
                                            <TableCell><div className="h-4 w-40 bg-muted animate-pulse rounded" /></TableCell>
                                            <TableCell><div className="h-4 w-40 bg-muted animate-pulse rounded" /></TableCell>
                                            <TableCell><div className="h-4 w-24 bg-muted animate-pulse rounded" /></TableCell>
                                            <TableCell><div className="h-4 w-32 bg-muted animate-pulse rounded" /></TableCell>
                                            <TableCell><div className="h-4 w-20 bg-muted animate-pulse rounded" /></TableCell>
                                            <TableCell><div className="h-4 w-40 bg-muted animate-pulse rounded" /></TableCell>
                                            <TableCell className="text-right w-24"><div className="h-4 w-24 bg-muted animate-pulse rounded ml-auto" /></TableCell>
                                        </TableRow>
                                    ))
                                ) : files.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                                            {t('gdriveFiles.noFilesFound', 'finance')}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    files.map((file) => (
                                        <TableRow key={file.id}>
                                            <TableCell className="font-medium max-w-120 truncate text-wrap" title={file.file_name}>
                                                {file.error_message ? (
                                                    <WarningIcon className="w-4 h-4 text-red-500 inline-block mr-2" >
                                                      <title>{file.error_message}</title>
                                                    </WarningIcon>
                                                ) : (
                                                    ''
                                                )}

                                                {file.file_name}</TableCell>
                                            <TableCell className="max-w-40 truncate" title={getVendorName(file.ai_result_json)}>
                                                {getVendorName(file.ai_result_json) || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {file.drive_modified_time ? new Date(file.drive_modified_time).toLocaleDateString('HU').replace(" ","").replace(' ','') : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {file.document_type ? (
                                                    <Badge variant={String(file.document_type) === 'delivery_note' ? 'secondary' : String(file.document_type) === 'bank_statement' ? 'secondary' : String(file.document_type) === 'payment_report' ? 'secondary' : 'outline'}>
                                                        {String(file.document_type) === 'delivery_note' ? t('gdriveFiles.documentTypes.deliveryNote', 'finance') : String(file.document_type) === 'bank_statement' ? t('gdriveFiles.documentTypes.bankStatement', 'finance') : String(file.document_type) === 'payment_report' ? t('gdriveFiles.documentTypes.paymentReport', 'finance') : t('gdriveFiles.documentTypes.invoice', 'finance')}
                                                    </Badge>
                                                ) : (
                                                    <span className="text-muted-foreground">—</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {file.extracted_invoice_number || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {getStatusBadge(file.processing_status)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex gap-2 justify-end">
                                                    {file.processing_status !== 'matched' && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => handleManualMatch(file)}
                                                        >
                                                            <Link className="h-4 w-4 mr-1" />
                                                            {t('gdriveFiles.manualMatch', 'finance')}
                                                        </Button>
                                                    )}
                                                    {file.matched_invoice_id ? (
                                                        <div className="flex flex-row gap-2">
                                                            <a href={`/invoices/${file.matched_invoice_id}`}>
                                                            <Button
                                                                size="sm"
                                                                    variant="secondary"
                                                                    className="text-primary border text-left text-sm"
                                                            >
                                                                {t('gdriveFiles.viewInvoice', 'finance')}
                                                            </Button>
                                                            </a>
                                                            <Button
                                                            size="sm"
                                                            variant="secondary"
                                                            onClick={() => handleManualMatch(file)}
                                                            className="text-primary border text-left text-sm"
                                                        >
                                                            {t('gdriveFiles.viewFile', 'finance')}
                                                        </Button>
                                                    </div>
                                                    ) : ("")}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    <PaginationFooter
                        pagination={pagination}
                        onPageChange={(page) => {
                            const newPagination = { page };
                            setPagination(prev => ({ ...prev, ...newPagination }));
                            updateURL(newPagination);
                        }}
                        onPageSizeChange={(pageSize) => {
                            const newPagination = { limit: pageSize, page: 1 };
                            setPagination(prev => ({ ...prev, ...newPagination }));
                            updateURL(newPagination);
                        }}
                    />
                </div>
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/invoices/gdrive-files')({
  component: GDriveFilesPage,
})
